import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Edit, Trash2, Eye, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { blogAPI } from '../services/api';

const AdminIntegrated = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [currentPost, setCurrentPost] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    category: '',
    tags: '',
    image: ''
  });

  useEffect(() => {
    loadPosts();
  }, []);

  const loadPosts = async () => {
    try {
      setLoading(true);
      const data = await blogAPI.getAllPosts();
      setPosts(data);
    } catch (error) {
      toast.error('Failed to load blog posts');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (post = null) => {
    if (post) {
      setCurrentPost(post);
      setFormData({
        title: post.title,
        slug: post.slug,
        excerpt: post.excerpt,
        content: post.content,
        category: post.category,
        tags: post.tags.join(', '),
        image: post.image
      });
    } else {
      setCurrentPost(null);
      setFormData({
        title: '',
        slug: '',
        excerpt: '',
        content: '',
        category: '',
        tags: '',
        image: ''
      });
    }
    setIsDialogOpen(true);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const postData = {
        ...formData,
        tags: formData.tags.split(',').map(t => t.trim())
      };

      if (currentPost) {
        // Update existing post
        await blogAPI.updatePost(currentPost.id, postData);
        toast.success('Blog post updated successfully!');
      } else {
        // Create new post
        await blogAPI.createPost(postData);
        toast.success('Blog post created successfully!');
      }
      
      setIsDialogOpen(false);
      loadPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save blog post');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this post?')) {
      try {
        await blogAPI.deletePost(id);
        toast.success('Blog post deleted successfully!');
        loadPosts();
      } catch (error) {
        toast.error('Failed to delete blog post');
      }
    }
  };

  return (
    <div className="min-h-screen pt-20 bg-[#1e1e1e]">
      {/* Header */}
      <section className="bg-gradient-to-br from-[#2a2a2a] to-[#320547] text-white py-12">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Blog Management</h1>
            <p className="text-xl text-gray-200">
              Create, edit, and manage blog posts
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
              <CardTitle className="text-2xl font-bold text-[#e0e0e0]">
                All Blog Posts ({posts.length})
              </CardTitle>
              <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    className="bg-[#320547] hover:bg-[#95228A] text-white"
                    onClick={() => handleOpenDialog()}
                  >
                    <Plus className="mr-2" size={18} /> New Post
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>
                      {currentPost ? 'Edit Blog Post' : 'Create New Blog Post'}
                    </DialogTitle>
                    <DialogDescription>
                      {currentPost
                        ? 'Update the details of your blog post'
                        : 'Fill in the details to create a new blog post'}
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleSubmit}>
                    <div className="space-y-4 py-4">
                      <div>
                        <label className="text-sm font-medium">Title *</label>
                        <Input
                          name="title"
                          value={formData.title}
                          onChange={handleChange}
                          required
                          placeholder="Enter blog post title"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium">Slug *</label>
                        <Input
                          name="slug"
                          value={formData.slug}
                          onChange={handleChange}
                          required
                          placeholder="url-friendly-slug"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium">Category *</label>
                        <Input
                          name="category"
                          value={formData.category}
                          onChange={handleChange}
                          required
                          placeholder="e.g., Artificial Intelligence"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium">Tags (comma-separated) *</label>
                        <Input
                          name="tags"
                          value={formData.tags}
                          onChange={handleChange}
                          required
                          placeholder="AI, Machine Learning, Technology"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium">Image URL *</label>
                        <Input
                          name="image"
                          value={formData.image}
                          onChange={handleChange}
                          required
                          placeholder="https://example.com/image.jpg"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium">Excerpt *</label>
                        <Textarea
                          name="excerpt"
                          value={formData.excerpt}
                          onChange={handleChange}
                          required
                          rows={3}
                          placeholder="Brief summary of the blog post"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium">Content *</label>
                        <Textarea
                          name="content"
                          value={formData.content}
                          onChange={handleChange}
                          required
                          rows={12}
                          placeholder="Full blog post content (supports markdown-style formatting)"
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setIsDialogOpen(false)}
                      >
                        Cancel
                      </Button>
                      <Button type="submit" className="bg-[#320547] hover:bg-[#95228A]">
                        {currentPost ? 'Update Post' : 'Create Post'}
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="animate-spin text-[#320547]" size={48} />
                </div>
              ) : (
                <div className="rounded-xl border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Title</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Author</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {posts.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                            No blog posts yet. Create your first post!
                          </TableCell>
                        </TableRow>
                      ) : (
                        posts.map((post) => (
                          <TableRow key={post.id}>
                            <TableCell className="font-medium">
                              <div className="max-w-xs">
                                <div className="font-semibold text-[#e0e0e0] truncate">
                                  {post.title}
                                </div>
                                <div className="text-sm text-gray-500 truncate">
                                  {post.excerpt}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="secondary">{post.category}</Badge>
                            </TableCell>
                            <TableCell>{post.date}</TableCell>
                            <TableCell>{post.author}</TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => window.open(`/blog/${post.slug}`, '_blank')}
                                >
                                  <Eye size={16} />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleOpenDialog(post)}
                                >
                                  <Edit size={16} />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => handleDelete(post.id)}
                                >
                                  <Trash2 size={16} />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default AdminIntegrated;
